export interface WorkItem {
  id?: string;
  task: string;
  notes: string;
  frequency: string;
  time_per_task: number | string;
  work_type: string;
  rating: string;
  user_id?: number;
  user_name?: string;
}

export interface SnapshotUser {
  id: number;
  user_id: number;
  weekly_hours: number;
}

interface Snapshot {
  id: number;
  name: string;
  users: SnapshotUser[];
}

interface EnumOptions {
  frequencies: Record<string, string>;
  work_types: Record<string, string>;
  ratings: Record<string, string>;
}

interface ApiResponse {
  snapshot: Snapshot;
  works: WorkItem[];
  team_works: WorkItem[];
  enums: EnumOptions;
}

// WorkTimeBreakdown has been moved to work_visualization.ts

// WorkVisualization class has been moved to work_visualization.ts
// ... existing code ...
import { WorkVisualization } from './work_visualization';

class SegmentedControl {
  private container: HTMLElement;
  private options: Record<string, string>;
  private name: string;
  private selectedValue: string;
  private onChange: (value: string) => void;
  private workId: string;

  constructor(
    container: HTMLElement, 
    options: Record<string, string>, 
    name: string, 
    selectedValue: string,
    workId: string,
    onChange: (value: string) => void
  ) {
    this.container = container;
    this.options = options;
    this.name = name;
    this.selectedValue = selectedValue;
    this.workId = workId;
    this.onChange = onChange;
    this.render();
  }

  private render() {
    const optionsHtml = Object.entries(this.options).map(([key, label], index, arr) => `
      <label class="group relative flex-1">
        <input type="radio" 
               name="${this.name}_${this.workId}" 
               value="${key}" 
               class="peer sr-only" 
               ${key === this.selectedValue ? 'checked' : ''}>
        <div class="absolute inset-0 ${index === 0 ? 'rounded-l-md' : ''} ${index === arr.length - 1 ? 'rounded-r-md' : ''} border border-gray-200 ${index > 0 ? '-ml-px' : ''} z-0 peer-checked:z-10 peer-checked:border-theme-600 peer-checked:bg-theme-600"></div>
        <div class="relative z-10 py-1.5 px-3 text-center text-sm font-medium text-gray-700 peer-checked:text-white">
          ${label}
        </div>
      </label>
    `).join('');

    this.container.innerHTML = `
      <div class="relative flex">
        ${optionsHtml}
      </div>
    `;

    this.attachListeners();
  }

  private attachListeners() {
    this.container.querySelectorAll('input[type="radio"]').forEach(input => {
      input.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        if (target.checked) {
          this.selectedValue = target.value;
          this.onChange(target.value);
        }
      });
    });
  }
}

class SnapshotWorksApp {
  private container: HTMLElement;
  private snapshotId: string;
  private userId: string;
  private data: ApiResponse | null = null;
  private deletedWorks: string[] = [];
  private segmentedControls: Map<string, SegmentedControl> = new Map();
  private visualization: WorkVisualization;

  constructor(container: HTMLElement) {
    this.container = container;
    this.snapshotId = container.dataset.snapshotId || '';
    this.userId = container.dataset.userId || '';
    this.visualization = new WorkVisualization(document.createElement('div'));
    this.initialize();
  }

  private async initialize() {
    await this.fetchData();
    this.render();
  }

  private async fetchData() {
    try {
      const response = await fetch(`/snapshots/${this.snapshotId}/users/${this.userId}/my_works.json`);
      if (!response.ok) throw new Error('Failed to fetch data');
      this.data = await response.json();
    } catch (error) {
      console.error('Error fetching data:', error);
      this.container.innerHTML = '<div class="text-red-600">Error loading work items</div>';
    }
  }

  private createSelectOptions(options: Record<string, string>, selectedValue: string): string {
    return Object.entries(options)
      .map(([key, label]) => `
        <option value="${key}" ${key === selectedValue ? 'selected' : ''}>
          ${label}
        </option>
      `).join('');
  }

  private createZeroStateHtml(): string {
    return `
      <div class="zero-state px-4 py-3 bg-gradient-to-r from-green-50 to-blue-50 border-b border-green-200">
        <div class="max-w-4xl mx-auto">
          <h3 class="text-sm font-medium text-green-900 mb-2">🚀 Let's capture your work!</h3>
          <p class="text-sm text-green-800 mb-3">
            <strong>Please enter your own work items first.</strong> Think about what you've been working on this week - your tasks, projects, meetings, and activities. Click "Add Work Item" to begin.
          </p>
          
          <div class="bg-white p-3 rounded-lg border border-green-200 mb-3">
            <div class="flex items-start">
              <div class="flex-shrink-0 mt-0.5">
                <svg class="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                </svg>
              </div>
              <div class="ml-2">
                <p class="text-xs text-green-800">
                  <strong>Tip:</strong> Start with your most time consuming tasks, then add from there.
                </p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
            <div class="bg-white p-2 rounded border border-blue-200">
              <span class="font-medium text-gray-900">Task:</span> What you're working on
            </div>
            <div class="bg-white p-2 rounded border border-blue-200">
              <span class="font-medium text-gray-900">Frequency:</span> How often you do this
            </div>
            <div class="bg-white p-2 rounded border border-blue-200">
              <span class="font-medium text-gray-900">Hours:</span> Time spent each time
            </div>
            <div class="bg-white p-2 rounded border border-blue-200">
              <span class="font-medium text-gray-900">Type:</span> What kind of work it is
            </div>
            <div class="bg-white p-2 rounded border border-blue-200">
              <span class="font-medium text-gray-900">Engagement:</span> Your preference level
            </div>
            <div class="bg-white p-2 rounded border border-blue-200">
              <span class="font-medium text-gray-900">Notes:</span> Any additional details
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private createWorkItemHtml(work?: WorkItem): string {
    if (!this.data) return '';

    const workId = work?.id || 'new-' + Date.now();
    const controlsContainerId = `controls-${workId}`;
    const defaultRatingKey = "medium";

    // Log the work item data for debugging
    console.log(`Creating work item HTML for work ${workId}:`, { 
      work, 
      rating: work?.rating || defaultRatingKey,
      enums: this.data.enums
    });

    return `
      <div class="work-item-row bg-white border-b border-gray-200" data-work-id="${workId}">
        <div class="px-4 py-3 space-y-2">
          <!-- Row 1: Task, Frequency, Hours, Type, Rating -->
          <div class="flex items-start gap-4">
            <div class="flex-1">
              <input type="text" 
                     name="snapshot_works[][task]" 
                     value="${work?.task || ''}"
                     placeholder="Task description"
                     class="block w-full px-2 py-2 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm font-medium" 
                     required>
            </div>

            <div class="w-32">
              <select name="snapshot_works[][frequency]" 
                      class="block w-full px-2 py-2 text-gray-900 focus:ring-0 sm:text-sm border border-gray-300 rounded-md">
                ${this.createSelectOptions(this.data.enums.frequencies, work?.frequency || Object.keys(this.data.enums.frequencies)[0])}
              </select>
            </div>

            <div class="w-20">
              <input type="number" 
                     name="snapshot_works[][time_per_task]" 
                     value="${work?.time_per_task !== undefined ? work.time_per_task : ''}"
                     placeholder="Hrs"
                     class="block w-full px-2 py-2 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm border border-gray-300 rounded-md" 
                     min="0" 
                     step="0.05">
            </div>

            <div class="w-32">
              <select name="snapshot_works[][work_type]" 
                      class="block w-full px-2 py-2 text-gray-900 focus:ring-0 sm:text-sm border border-gray-300 rounded-md">
                ${this.createSelectOptions(this.data.enums.work_types, work?.work_type || Object.keys(this.data.enums.work_types)[0])}
              </select>
            </div>

            <div class="flex-1">
              <div id="${controlsContainerId}-rating" class="rating-control"></div>
              <input type="hidden" name="snapshot_works[][rating]" value="${work?.rating || defaultRatingKey}">
            </div>

            <button type="button" class="delete-work-item text-gray-400 hover:text-red-600">
              <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>

          <!-- Row 2: Notes -->
          <div class="flex items-start gap-4">
            <div class="flex-1">
              <input type="text"
                     name="snapshot_works[][notes]" 
                     value="${work?.notes || ''}"
                     placeholder="Notes (optional)"
                     class="block w-full px-2 py-2 text-gray-600 placeholder:text-gray-400 focus:ring-0 sm:text-sm">
            </div>
            <div class="w-5"></div>
          </div>
          ${work?.id ? `<input type="hidden" name="snapshot_works[][id]" value="${work.id}">` : ''}
        </div>
      </div>
    `;
  }

  private initializeSegmentedControls(workItemElement: HTMLElement, work?: WorkItem) {
    if (!this.data) return;

    const workId = workItemElement.getAttribute('data-work-id') || 'new-' + Date.now();
    const ratingControlElement = workItemElement.querySelector(`#controls-${workId}-rating`);
    const ratingInputElement = workItemElement.querySelector('input[name="snapshot_works[][rating]"]') as HTMLInputElement;

    if (ratingControlElement instanceof HTMLElement) {
      // Define mapping from backend rating keys to UI display labels
      const ratingKeyToDisplayLabel: Record<string, string> = {
        "low": "Less",
        "medium": "Same",
        "high": "More"
      };

      // The options for SegmentedControl: keys are backend rating keys, values are UI display labels
      const segmentedControlOptions = ratingKeyToDisplayLabel;

      // Determine the selected backend rating key for the current work item.
      // Default to "medium" (backend key) if not present or for new items.
      // work?.rating will be 'low', 'medium', or 'high' from the backend.
      const selectedBackendKey = work?.rating || "medium"; 

      console.log(`Initializing rating control for work ${workId}:`, { 
        workRatingBackendKey: work?.rating, 
        selectedBackendKeyForControl: selectedBackendKey,
        currentHiddenInputValue: ratingInputElement.value,
        optionsForControl: segmentedControlOptions
      });

      this.segmentedControls.set(`${workId}-rating`, new SegmentedControl(
        ratingControlElement,
        segmentedControlOptions,
        'rating',
        selectedBackendKey,
        workId,
        (newBackendKeyValue) => {
          console.log(`Rating changed for work ${workId}:`, { 
            newBackendKeyValue: newBackendKeyValue, 
            oldHiddenInputValue: ratingInputElement.value,
          });
          ratingInputElement.value = newBackendKeyValue;
        }
      ));
    }
  }

  private render() {
    if (!this.data) return;

    this.container.innerHTML = `
      <div id="work-visualization"></div>
      <div class="bg-white shadow sm:rounded-lg">
        <form id="work-items-form">
          ${this.data.works.length === 0 ? this.createZeroStateHtml() : ''}

          <div class="border-b border-gray-200 bg-gray-50">
            <div class="px-4 py-2 space-y-2">
              <!-- Row 1 Headers -->
              <div class="flex items-center gap-4">
                <div class="flex-1 text-xs font-medium text-gray-500 uppercase tracking-wider">Task</div>
                <div class="w-32 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</div>
                <div class="w-20 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</div>
                <div class="w-32 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Type</div>
                <div class="flex-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">I wish I could do this task</div>
                <div class="w-5"></div>
              </div>
              <!-- Row 2 Headers -->
              <div class="flex items-center gap-4">
                <div class="flex-1 text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</div>
                <div class="w-5"></div>
              </div>
            </div>
          </div>

          <div id="work-items-container" class="divide-y divide-gray-200">
            ${this.data.works.map(work => this.createWorkItemHtml(work)).join('')}
          </div>

          <div class="px-4 py-3 bg-gray-50 flex justify-between items-center">
            <button type="button" id="add-work-item" class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-theme-500">
              <svg class="-ml-1 mr-2 h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
              </svg>
              Add Work Item
            </button>

            <div class="flex gap-3">
              <a href="/snapshots/${this.snapshotId}" class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-theme-500">
                Cancel
              </a>
              <button type="submit" class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-theme-600 hover:bg-theme-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-theme-500">
                Save All Changes
              </button>
            </div>
          </div>

          <input type="hidden" name="deleted_works[]" id="deleted-works" value="">
        </form>
      </div>
    `;

    // Initialize visualization with both personal and team data
    const visualizationContainer = this.container.querySelector('#work-visualization');
    if (visualizationContainer instanceof HTMLElement) {
      this.visualization = new WorkVisualization(visualizationContainer);
      this.visualization.update(this.data.works, this.data.team_works, this.data.snapshot.users);
    }

    // Initialize segmented controls for existing work items
    this.container.querySelectorAll('.work-item-row').forEach((workItem) => {
      const workId = workItem.getAttribute('data-work-id');
      const work = this.data?.works.find(w => w.id?.toString() === workId);
      this.initializeSegmentedControls(workItem as HTMLElement, work);
    });

    this.attachEventListeners();
  }

  private attachEventListeners() {
    const form = this.container.querySelector('#work-items-form') as HTMLFormElement;
    const addButton = this.container.querySelector('#add-work-item');
    const workItemsContainer = this.container.querySelector('#work-items-container');
    const deletedWorksInput = this.container.querySelector('#deleted-works') as HTMLInputElement;

    // Add new work item
    addButton?.addEventListener('click', () => {
      if (!workItemsContainer) return;

      const newWorkHtml = this.createWorkItemHtml();
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = newWorkHtml;
      const newWorkElement = tempContainer.firstElementChild;
      if (newWorkElement) {
        workItemsContainer.appendChild(newWorkElement);
        this.initializeSegmentedControls(newWorkElement as HTMLElement);
        this.attachDeleteListener(newWorkElement as HTMLElement);
      }
    });

    // Add delete functionality to existing work items
    this.container.querySelectorAll('.work-item-row').forEach(workItem => {
      this.attachDeleteListener(workItem as HTMLElement);
    });

    // Form submission
    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const formData = new FormData(form);
      formData.append('deleted_works', JSON.stringify(this.deletedWorks));

      try {
        const response = await fetch(
          `/snapshots/${this.snapshotId}/users/${this.userId}/my_works/bulk_update`,
          {
            method: 'POST',
            body: formData,
            headers: {
              'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
            },
            credentials: 'same-origin'
          }
        );

        if (!response.ok) throw new Error('Form submission failed');
        window.location.reload();
      } catch (error) {
        console.error('Error:', error);
        alert('An error occurred while saving the work items. Please try again.');
      }
    });

    // Add event listeners for work item changes
    const updateVisualization = () => {
      if (!this.data) return;
      
      const formData = new FormData(form);
      const entries = Array.from(formData.entries());
      
      // First, group all fields by their position in the form
      const groupedFields: Record<string, Partial<WorkItem>> = {};
      let currentGroup = '';
      
      entries.forEach(([key, value]) => {
        // Skip the deleted_works field and rating_X fields
        if (key === 'deleted_works[]' || key.startsWith('rating_')) {
          return;
        }

        const match = key.match(/snapshot_works\[\]\[(\w+)\]/);
        if (!match) return;

        const [, field] = match;
        
        // Start a new group when we see a task field
        if (field === 'task') {
          currentGroup = value as string; // Use task as temporary group key
          groupedFields[currentGroup] = {};
        }
        
        if (currentGroup && field) {
          const currentItem = groupedFields[currentGroup];
          if (field === 'time_per_task') {
            currentItem[field] = parseFloat(value as string) || 0;
          } else if (field === 'task' || field === 'notes' || field === 'frequency' || field === 'work_type' || field === 'rating' || field === 'id') {
            currentItem[field] = value as string;
          }
        }
      });

      // Convert grouped fields to work items
      const works: WorkItem[] = [];
      Object.values(groupedFields).forEach((item) => {
        if (
          typeof item.task === 'string' &&
          typeof item.notes === 'string' &&
          typeof item.frequency === 'string' &&
          typeof item.work_type === 'string' &&
          typeof item.rating === 'string' &&
          typeof item.time_per_task === 'number'
        ) {
          works.push(item as WorkItem);
        }
      });

      this.visualization.update(works, this.data.team_works, this.data.snapshot.users);
    };

    // Add change event listeners to all form inputs
    form?.querySelectorAll('input, select').forEach(input => {
      input.addEventListener('change', updateVisualization);
      input.addEventListener('input', updateVisualization);
    });
  }

  private attachDeleteListener(workItem: HTMLElement) {
    const deleteButton = workItem.querySelector('.delete-work-item');
    deleteButton?.addEventListener('click', () => {
      const workId = workItem.dataset.workId;
      if (workId) {
        this.deletedWorks.push(workId);
      }
      workItem.remove();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('snapshot-works-app');
  if (container) {
    new SnapshotWorksApp(container);
  }
}); 